import type { LegalPageContent } from '@/types/pageContent';

export const DEFAULT_RENTAL_AGREEMENT: LegalPageContent = {
    lastUpdated: '2025-12-01',
    sections: [
        {
            heading: 'Agreement Overview',
            style: 'default',
            items: [
                {
                    title: '',
                    body: 'This Rental Agreement ("Agreement") is entered into between Stork Rentals S.L. ("Company", "we", "us") and the individual or entity renting equipment ("Renter", "you"). By completing a rental booking, you acknowledge that you have read, understood, and agree to be bound by the terms of this Agreement.',
                },
                {
                    title: '',
                    body: 'This Agreement applies to all equipment rented through our platform, whether for short-term fixed-date rentals or flexible rolling monthly rentals.',
                },
            ],
        },
        {
            heading: 'Equipment Responsibility & Care',
            style: 'default',
            items: [
                {
                    title: 'Proper Use',
                    body: 'You agree to use all rented equipment solely for its intended purpose and in accordance with the manufacturer\'s guidelines and age/weight recommendations.',
                },
                {
                    title: 'Supervision',
                    body: 'Children must be supervised at all times when using rented equipment. You are responsible for ensuring that equipment is used safely and appropriately.',
                },
                {
                    title: 'Care & Maintenance',
                    body: 'You agree to take reasonable care of the equipment during the rental period, keeping it clean and protected from extreme conditions, misuse, or neglect.',
                },
                {
                    title: 'Modifications Prohibited',
                    body: 'You must not modify, disassemble, repair, or alter any rented equipment. If equipment requires repair, contact us immediately.',
                },
            ],
        },
        {
            heading: 'Inspection & Acceptance',
            style: 'default',
            items: [
                {
                    title: 'Condition at Pickup',
                    body: 'All equipment is cleaned, sanitized, and inspected before each rental. You should inspect equipment at the time of pickup or delivery.',
                },
                {
                    title: 'Reporting Defects',
                    body: 'Any defects, damage, or missing parts must be reported to us within 24 hours of receiving the equipment. Equipment accepted without a reported issue is deemed to have been received in good working condition.',
                },
                {
                    title: 'Replacement',
                    body: 'If equipment is found to be defective at the time of pickup, we will provide a replacement or full refund at our discretion.',
                },
            ],
        },
        {
            heading: 'Rental Duration & Extensions',
            style: 'default',
            items: [
                {
                    title: 'Rental Period',
                    body: 'The rental period begins at the scheduled pickup or delivery time and ends at the scheduled return time as specified in your booking confirmation.',
                },
                {
                    title: 'Extensions',
                    body: 'Rental extensions must be requested at least 24 hours before the scheduled return date. Extensions are subject to equipment availability and will be charged at the applicable daily or monthly rate.',
                },
                {
                    title: 'Early Returns',
                    body: 'Equipment may be returned before the scheduled end date. However, no refunds are provided for unused rental days on short-term rentals.',
                },
            ],
        },
        {
            heading: 'Deposit & Payment Terms',
            style: 'default',
            items: [
                {
                    title: 'Security Deposit',
                    body: 'A refundable security deposit is required for all rentals. The deposit is held as a pre-authorization on your payment card and is not charged unless damage, loss, or excessive cleaning is required.',
                },
                {
                    title: 'Deposit Release',
                    body: 'The security deposit authorization is released within 5-10 business days after the equipment is returned in good condition with all parts and accessories.',
                },
                {
                    title: 'Payment',
                    body: 'For short-term rentals, the full rental amount is charged at the time of booking. For monthly rentals, an initial payment covers the first month, followed by recurring monthly charges until the equipment is returned.',
                },
                {
                    title: 'Accepted Methods',
                    body: 'We accept major credit and debit cards (Visa, Mastercard, American Express) through our secure payment processor. All transactions are encrypted.',
                },
            ],
        },
        {
            heading: 'Damage, Loss & Theft',
            style: 'default',
            items: [
                {
                    title: 'Liability for Damage',
                    body: 'You are responsible for any damage to the equipment beyond normal wear and tear during the rental period. Repair or replacement costs will be deducted from your security deposit.',
                },
                {
                    title: 'Loss or Theft',
                    body: 'In the event of loss or theft of rented equipment, you are liable for the full replacement cost of the item. You must report any loss or theft to us immediately.',
                },
                {
                    title: 'Reporting Obligations',
                    body: 'Any damage to equipment during the rental period must be reported to us as soon as possible. In the case of theft, a police report may be required.',
                },
                {
                    title: 'Normal Wear & Tear',
                    body: 'We understand that baby equipment is used actively. Minor scuffs, surface marks, and normal wear consistent with proper use are not considered damage.',
                },
            ],
        },
        {
            heading: 'Return Conditions',
            style: 'default',
            items: [
                {
                    title: 'Condition on Return',
                    body: 'Equipment must be returned in the same condition as received, allowing for normal wear and tear. All parts, accessories, and components must be included.',
                },
                {
                    title: 'Cleaning',
                    body: 'While professional cleaning is performed by us between rentals, equipment should be returned in a reasonably clean state. Excessive cleaning fees may apply.',
                },
                {
                    title: 'Late Returns',
                    body: 'Equipment returned after the scheduled return time will incur additional daily charges at the standard daily rate unless an extension has been arranged.',
                },
                {
                    title: 'Return Location',
                    body: 'Equipment must be returned to the same location from which it was collected, or as otherwise agreed with us.',
                },
            ],
        },
        {
            heading: 'Limitation of Liability',
            style: 'default',
            items: [
                {
                    title: '',
                    body: 'To the fullest extent permitted by law, Stork Rentals shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to the use of rented equipment.',
                },
                {
                    title: '',
                    body: 'Our total liability for any claim arising from this Agreement shall not exceed the total rental fees paid for the specific booking in question.',
                },
                {
                    title: '',
                    body: 'You acknowledge that the safety of children using the equipment is solely your responsibility, and that Stork Rentals is not liable for any injury resulting from improper use, lack of supervision, or failure to follow manufacturer guidelines.',
                },
            ],
        },
        {
            heading: 'Governing Law',
            style: 'default',
            items: [
                {
                    title: '',
                    body: 'This Agreement is governed by and construed in accordance with the laws of Spain. Any disputes arising from or related to this Agreement shall be subject to the exclusive jurisdiction of the courts of Barcelona, Spain.',
                },
            ],
        },
    ],
};
